<template>
  <div>
    <b-card
      no-body
    >
      <div>
        <div
          class="m-2"
        >
          <!-- input -->
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label>Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1 width-100 mr-1"
              />
              <b-button
                v-can="'create-merchant-address'"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                class="custom-button-color"
                @click="onClickOpenAddBusinessAddressModal"
              >
                <feather-icon icon="PlusIcon" />
                Add New Business Address
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- table -->
        <b-overlay
          id="overlay-background"
          variant="light"
          opacity="0.30"
          blur="10px"
          rounded="sm"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="rows"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            class="hide-vertical-scroll"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #cell(business_name)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  size="32"
                  :text="avatarText(data.item.name)"
                  variant="light-primary"
                />
                <div>
                  <div class="font-weight-bolder ml-1">
                    {{ data.item.business_name }}
                  </div>
                  <div class="font-small-2 ml-1">
                    {{ data.item.ref_no }}
                  </div>
                </div>
              </div>
            </template>
            <template #cell(address)="data">
              <div class="d-flex align-items-center">
                <div>
                  <div class="font-weight-bolder ml-1">
                    {{ data.item.address_line_1 }} {{ data.item.address_line_2 }} {{ data.item.address_line_3 }}
                  </div>
                </div>
              </div>
            </template>

            <template #cell(is_default)="data">
              <b-badge :variant="data.value ?'light-success':'light-danger'">
                {{ data.value ? 'Yes' : 'No' }}
              </b-badge>
            </template>

            <template #cell(action)="data">
              {{ data.value }}
              <b-dropdown
                v-can="('edit-merchant-address' && 'show-merchant-address') || 'activate-merchant-address' || 'deactivate-merchant-address' || 'show-merchant-address'"
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="12"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  @click="onClickOpenManageBusinessAddressModal(data.item.id)"
                >
                  <feather-icon icon="ToolIcon" />
                  Manage Address
                </b-dropdown-item>
                <!--                <b-dropdown-item-->
                <!--                  @click="onClickOpenDeleteBusinessAddressConfirm(data.item.id)"-->
                <!--                >-->
                <!--                  <feather-icon icon="ToolIcon" />-->
                <!--                  Delete Address-->
                <!--                </b-dropdown-item>-->
              </b-dropdown>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="0"
                  :per-page="perPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </div>
    </b-card>

    <create-merchant-business-address-modal ref="add_new_merchant_business_address_modal" />
    <update-merchant-business-address-modal ref="update_merchant_business_address_modal" />
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BOverlay,
  VBModal,
  BTable,
  BDropdown,
  BDropdownItem,
  BBadge,
  BAvatar,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import vSelect from 'vue-select'
import { avatarText } from '@core/utils/filter'
import { timestampToDateTime } from '@core/utils/timeUtil'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import store from '@/store'
import CreateMerchantBusinessAddressModal from '@/components/merchant/CreateMerchantBusinessAddressModal.vue'
import UpdateMerchantBusinessAddressModal from '@/components/merchant/UpdateMerchantBusinessAddressModal.vue'

const MerchantRepository = RepositoryFactory.get('merchant')

function initialState() {
  return {
    avatarText,
    timestampToDateTime,
    total: 0,
    meta: {},
    dataLoading: false,
    sort: '',
    perPage: 20,
    pageOptions: [10, 20, 50],
    totalRows: 1,
    page: 1,
    sortBy: '',
    sortDesc: false,
    sortDirection: 'asc',
    fields: [
      {
        key: 'id', label: 'Ref No', sortable: true, thClass: 'd-none', tdClass: 'd-none',
      },
      { key: 'business_name', label: 'Location', sortable: true },
      { key: 'address', label: 'Address', sortable: false },
      { key: 'city.name', label: 'City', sortable: false },
      { key: 'is_default', label: 'Is Default', sortable: false },
      { key: 'action', label: 'Action', sortable: false },
    ],
    rows: [],
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BPagination,
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BOverlay,
    vSelect,
    BDropdown,
    BDropdownItem,
    BBadge,
    BAvatar,
    CreateMerchantBusinessAddressModal,
    UpdateMerchantBusinessAddressModal,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  props: {
    approvalGroupStatus: {
      type: String,
      required: true,
    },
    approvalType: {
      type: Object,
      required: true,
    },
  },
  data() {
    return initialState()
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    page() {
      this.fetchMerchantBusinessAddressList()
    },
    perPage() {
      this.fetchMerchantBusinessAddressList()
    },
  },
  mounted() {
    // Set the initial number of items
    this.fetchMerchantBusinessAddressList()
  },
  methods: {
    onClickRefresh() {
      this.fetchMerchantBusinessAddressList()
    },
    onClickOpenAddBusinessAddressModal() {
      this.$refs.add_new_merchant_business_address_modal.openModal(this.$route.params.id, this.$route.params.businessId)
    },
    onClickOpenManageBusinessAddressModal(id) {
      this.$refs.update_merchant_business_address_modal.openModal(this.$route.params.id, this.$route.params.businessId, id, this.approvalGroupStatus, this.approvalType)
    },
    onClickOpenDeleteBusinessAddressConfirm() {
      // Set Merchant Business Address Delete Confirm Modal
    },
    async fetchMerchantBusinessAddressList() {
      this.dataLoading = true
      this.rows = []
      try {
        const { data } = (await MerchantRepository.getMerchantAddressList(this.$route.params.businessId))
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },

    // handleOk() {
    //   if (this.cName !== ''
    //       && this.pCode !== ''
    //       && this.Stateselected !== null
    //       && this.conselected !== null
    //       && this.wareelected !== null
    //       && this.default_warehouse_selected !== null) {
    //     this.setDataforCity()
    //   } else {
    //     this.showErrorMessage('Complete All Fields Please')
    //   }
    // },

    handleChangePage(page) {
      this.page = page
      this.fetchMerchantBusinessAddressList()
    },
    changePage(value) {
      this.perPage = value
    },
    // REQUIRED
  },
}
</script>
<style lang="scss">
@import '../../@core/scss/vue/libs/vue-select.scss';
@import '../../@core/scss/button-color.scss';
</style>
